import { cloneElement, useRef, useState } from 'react';
import { Box, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import CalendarHeatmap from 'react-calendar-heatmap';
import dayjs from 'dayjs';

import type { KeyboardEvent, ReactElement, SVGProps } from 'react';

export type ActivityDay = { key: string; count: number };
export function summarizeActivity(days: ActivityDay[]) {
    let total = 0;
    let bestDay = 0;
    let activeDays = 0;
    let currentStreak = 0;
    let longestStreak = 0;
    days.forEach(({ count }) => {
        total += count;
        bestDay = Math.max(bestDay, count);
        if (count > 0) {
            activeDays += 1;
            currentStreak += 1;
            longestStreak = Math.max(longestStreak, currentStreak);
        } else {
            currentStreak = 0;
        }
    });
    return { total, bestDay, activeDays, currentStreak, longestStreak };
}

export const leadCountLabel = (count: number) => {
    const tail = count % 100;
    const word =
        tail >= 11 && tail <= 14
            ? 'лидов'
            : count % 10 === 1
            ? 'лид'
            : count % 10 >= 2 && count % 10 <= 4
            ? 'лида'
            : 'лидов';
    return `${new Intl.NumberFormat('ru-RU').format(count)} ${word}`;
};
const dateLabel = (key: string) =>
    new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).format(dayjs(key).toDate());
const shortDate = (key: string) =>
    new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' }).format(dayjs(key).toDate()).replace('.', '');
const months: [string, string, string, string, string, string, string, string, string, string, string, string] = [
    'Янв',
    'Фев',
    'Мар',
    'Апр',
    'Май',
    'Июн',
    'Июл',
    'Авг',
    'Сен',
    'Окт',
    'Ноя',
    'Дек',
];

export default function StreamActivityCalendar({ days }: { days: ActivityDay[] }) {
    const root = useRef<HTMLDivElement>(null);
    const [focusedDate, setFocusedDate] = useState('');
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
    const visibleDays = isNarrow ? days.slice(-90) : days;
    const colors =
        theme.palette.mode === 'dark'
            ? ['var(--lm-hover)', '#233f70', '#315d9f', '#477fe2', '#80aaff']
            : ['var(--lm-hover)', '#abc2ff', '#779fff', '#3b6ee9', 'var(--lm-accent)'];
    if (!visibleDays.length) return null;
    const first = visibleDays[0].key;
    const last = visibleDays[visibleDays.length - 1].key;
    const total = summarizeActivity(visibleDays).total;
    const maximum = Math.max(...visibleDays.map((day) => day.count), 1);
    const weeks = Math.ceil((dayjs(first).day() + visibleDays.length) / 7);
    // v1.10 reserves 30 SVG units below a horizontal calendar for weekday labels.
    // Those labels sit on the left; remove only this unused space, at any screen width.
    const unusedHeightPercent = (30 / (weeks * 12.5 - 2.5 + 30)) * 100;
    const level = (count: number) => (count > 0 ? Math.max(1, Math.min(4, Math.ceil((count / maximum) * 4))) : 0);
    // Local noon prevents UTC date shifts and DST rounding in the calendar's day lookup.
    // Its startDate is exclusive, so include the day before our first result.
    const values = visibleDays.map((day) => ({ date: dayjs(day.key).hour(12).toDate(), key: day.key, count: day.count }));
    const moveFocus = (event: KeyboardEvent<SVGRectElement>, key: string) => {
        const index = visibleDays.findIndex((day) => day.key === key);
        const delta: Record<string, number> = { ArrowLeft: -7, ArrowRight: 7, ArrowUp: -1, ArrowDown: 1 };
        const next =
            event.key === 'Home'
                ? 0
                : event.key === 'End'
                ? visibleDays.length - 1
                : delta[event.key] !== undefined
                ? index + delta[event.key]
                : null;
        if (next === null) return;
        event.preventDefault();
        const day = visibleDays[Math.max(0, Math.min(visibleDays.length - 1, next))];
        root.current?.querySelector<SVGRectElement>(`[data-date="${day.key}"]`)?.focus();
    };
    return (
        <Box
            ref={root}
            role="group"
            aria-label={`Активность потока: ${leadCountLabel(total)} за ${visibleDays.length} дней`}
            sx={{ width: '100%', minWidth: 0 }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px 12px',
                    justifyContent: 'space-between',
                    mb: 2,
                    alignItems: 'baseline',
                }}
            >
                <Typography sx={{ fontSize: 13, color: 'var(--lm-muted)' }}>
                    <Box
                        component="span"
                        sx={{ fontWeight: 600, color: 'var(--lm-text)' }}
                    >
                        {leadCountLabel(total)}
                    </Box>{' '}
                    за {visibleDays.length} дней
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--lm-muted)' }}>
                    {shortDate(first)} — {shortDate(last)}
                </Typography>
            </Box>
            <Box
                sx={{
                    '& .react-calendar-heatmap': {
                        width: '100%',
                        display: 'block',
                        overflow: 'visible',
                        marginBottom: `-${unusedHeightPercent}%`,
                    },
                    '& .react-calendar-heatmap text': { fill: 'var(--lm-muted)', fontFamily: 'inherit', fontSize: 7 },
                    '& .react-calendar-heatmap rect': {
                        outline: 'none',
                        stroke: 'var(--lm-surface)',
                        strokeWidth: 0.5,
                    },
                    '& .react-calendar-heatmap rect:hover, & .react-calendar-heatmap rect:focus-visible': {
                        stroke: 'var(--lm-text)',
                        strokeWidth: 1,
                    },
                    '& .activity-0': { fill: colors[0] },
                    '& .activity-1': { fill: colors[1] },
                    '& .activity-2': { fill: colors[2] },
                    '& .activity-3': { fill: colors[3] },
                    '& .activity-4': { fill: colors[4] },
                }}
            >
                <CalendarHeatmap
                    startDate={dayjs(first).subtract(1, 'day').hour(12).toDate()}
                    endDate={dayjs(last).hour(12).toDate()}
                    values={values}
                    gutterSize={2.5}
                    showWeekdayLabels
                    weekdayLabels={['', 'Пн', '', 'Ср', '', 'Пт', '']}
                    monthLabels={months}
                    showOutOfRangeDays={false}
                    classForValue={(value) => `activity-${level(value?.count || 0)}`}
                    transformDayElement={(element, value, index) => {
                        if (!value?.key) return null;
                        const label = `${leadCountLabel(value.count)} · ${dateLabel(value.key)}`;
                        return (
                            <Tooltip
                                key={index}
                                title={label}
                                placement="top"
                                arrow
                                enterDelay={80}
                                enterTouchDelay={0}
                                leaveTouchDelay={2500}
                            >
                                {cloneElement(
                                    element as ReactElement<SVGProps<SVGRectElement>>,
                                    {
                                        rx: 2,
                                        ry: 2,
                                        role: 'img',
                                        'aria-label': label,
                                        tabIndex: value.key === (focusedDate || last) ? 0 : -1,
                                        onFocus: () => setFocusedDate(value.key),
                                        onKeyDown: (event) => moveFocus(event, value.key),
                                        ...{ 'data-date': value.key },
                                    },
                                    null
                                )}
                            </Tooltip>
                        );
                    }}
                />
            </Box>
            <Box
                aria-hidden="true"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 0.5,
                    mt: 1,
                    color: 'var(--lm-muted)',
                    fontSize: 11,
                }}
            >
                <Box
                    component="span"
                    sx={{ mr: 0.25 }}
                >
                    Меньше
                </Box>
                {colors.map((color) => (
                    <Box
                        key={color}
                        sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '2px' }}
                    />
                ))}
                <Box
                    component="span"
                    sx={{ ml: 0.25 }}
                >
                    Больше
                </Box>
            </Box>
        </Box>
    );
}
